// ============================================================================
// badges.js — the old dashboard's colours, copied value for value.
//
// Every palette and every band below was read out of the old app's own bundle
// rather than matched by eye, because "the same" has to mean the same number:
// an agent on 91.3% adherence is amber there, and would have been green under
// the portal's own bands (90/85). The old ones win.
//
//   pod badge      ui/podColors — one colour per pod, grey for anything else
//   channel dot    Voice blue, Callback sky, VIP cyan, Email amber, ...
//   avatar         ten solid colours, chosen by a hash of the name
//   adherence      >= 92 green, >= 90 amber, below red
//   efficiency     >= 92 green, >= 87 amber, below red (thresholds.js)
//   QA and CSAT    >= 90 green, >= 80 amber, below red — and grey under three
//                  responses, which is too few to judge
//
// Tailwind class names there, hex here: the portal styles inline. The mapping
// is Tailwind's own palette (emerald-50 #ECFDF5, and so on).
// ============================================================================

const GREY = { bg: '#F1F5F9', fg: '#334155', dot: '#64748B', line: '#64748B' };

export const POD_TONE = {
  'Semi-Fran': { bg: '#EFF6FF', fg: '#1D4ED8', dot: '#3B82F6', line: '#2563EB' },
  RecycLA:     { bg: '#ECFDF5', fg: '#047857', dot: '#10B981', line: '#10B981' },
  TaxRoll:     { bg: '#FFF7ED', fg: '#C2410C', dot: '#F97316', line: '#F97316' },
  OpenMarket:  { bg: '#F5F3FF', fg: '#6D28D9', dot: '#8B5CF6', line: '#8B5CF6' },
  GroupA:      { bg: '#FFF1F2', fg: '#BE123C', dot: '#F43F5E', line: '#F43F5E' },
  GroupB:      { bg: '#FFFBEB', fg: '#B45309', dot: '#F59E0B', line: '#F59E0B' },
  GroupC:      { bg: '#ECFEFF', fg: '#0E7490', dot: '#06B6D4', line: '#06B6D4' },
  LACO:        { bg: '#F1F5F9', fg: '#334155', dot: '#64748B', line: '#475569' },
  IRG:         { bg: '#F7FEE7', fg: '#4D7C0F', dot: '#84CC16', line: '#84CC16' },
};

/** A pod's colours. A pod the old app never had one for is grey, not absent. */
export const podTone = (pod) => POD_TONE[pod] || GREY;

export const CHANNEL_DOT = {
  Voice: '#3B82F6', Callback: '#38BDF8', VIP: '#06B6D4',
  Email: '#F59E0B', Chat: '#8B5CF6', Voicemail: '#F43F5E',
};
export const channelDot = (channel) => CHANNEL_DOT[channel] || '#94A3B8';

const AVATAR = ['#3B82F6', '#10B981', '#F97316', '#8B5CF6', '#F43F5E',
  '#F59E0B', '#06B6D4', '#6366F1', '#EC4899', '#14B8A6'];

/** "Ana Maria Ruiz" -> "AR"; one word -> its first two letters; none -> "??". */
export function initials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  return (parts.length === 1
    ? parts[0].slice(0, 2)
    : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** The same name always gets the same colour: the old app's string hash. */
export function avatarColor(name) {
  if (!name) return AVATAR[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR[Math.abs(h) % AVATAR.length];
}

// Chip and text tones. `chip` carries a background, `ink` is text only — the
// old table tinted adherence and left efficiency, QA and CSAT as coloured
// numbers.
export const TONE = {
  good:    { bg: '#ECFDF5', fg: '#047857' },
  warn:    { bg: '#FFFBEB', fg: '#B45309' },
  bad:     { bg: '#FFF1F2', fg: '#BE123C' },
  neutral: { bg: '#F1F5F9', fg: '#64748B' },
};
export const INK = { good: '#047857', warn: '#D97706', bad: '#BE123C', neutral: '#64748B' };

export const adherenceTone = (v) => (v == null ? null : v >= 92 ? 'good' : v >= 90 ? 'warn' : 'bad');
export const efficiencyTone = (v) => (v == null ? null : v >= 92 ? 'good' : v >= 87 ? 'warn' : 'bad');
/** Under three responses the score is shown, but grey: too few to judge. */
export const scoreTone = (v, n) => (v == null ? null : !(n >= 3) ? 'neutral' : v >= 90 ? 'good' : v >= 80 ? 'warn' : 'bad');
